import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import { BORDER_RADIUS } from '@/constants/config';
import { useModalStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';

const createStyles = (colors, isXS, noPadding, alignLeft) => StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: noPadding ? 0 : isXS ? 20 : 40,
    maxWidth: 640,
    alignSelf: 'center',
    width: '100%',
  },
  content: {
    width: '100%',
    backgroundColor: colors.solid_color_primary,
    borderRadius: BORDER_RADIUS.large,
    padding: isXS ? 24 : 40,
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundIcon: {
    position: 'absolute',
    top: -20,
    right: -20,
    opacity: 0.03,
    transform: [{ rotate: '12deg' }],
  },
  iconContainer: {
    width: isXS ? 56 : 64,
    height: isXS ? 56 : 64,
    borderRadius: isXS ? 28 : 32,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  header: {
    alignItems: alignLeft ? 'flex-start' : 'center',
    marginBottom: 25,
  },
  title: {
    textAlign: alignLeft ? 'left' : 'center',
    marginBottom: 12,
    fontSize: isXS ? 24 : 28,
    lineHeight: isXS ? 32 : 36,
  },
  subtitle: {
    textAlign: alignLeft ? 'left' : 'center',
    marginBottom: 0,
    maxWidth: 480,
    opacity: 0.8,
  },
  features: {
    width: '100%',
    marginBottom: 25,
    maxWidth: 480,
  },
  feature: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingHorizontal: isXS || alignLeft ? 0 : 12,
  },
  featureIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  featureContent: {
    flex: 1,
  },
  featureTitle: {
    marginBottom: 2,
    color: colors.text_color_primary,
  },
  featureDescription: {
    opacity: 0.7,
  },
  buttonContainer: {
    width: '100%',
    flexDirection: 'row',
  },
  actionButton: {
    minWidth: isXS ? '100%' : 240,
    height: isXS ? 48 : 52,
  },
  actionButtonText: {
    fontSize: isXS ? 16 : 17,
  }
});

const UpgradeContainer = ({
  title,
  subtitle,
  features,
  icon = "sparkles",
  actionText,
  onAction,
  plusTitle,
  plusSubtitle,
  plusFeature,
  noPadding,
  alignLeft,
}) => {
  const { t } = useTranslation();
  const displayTitle = title ?? t('upgrade.title');
  const displaySubtitle = subtitle ?? t('upgrade.subtitle');
  const displayActionText = actionText ?? t('upgrade.button');
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);
  const styles = useMemo(() => createStyles(colors, isXS, noPadding, alignLeft), [colors, isXS, noPadding, alignLeft]);

  const handleOpenModal = useCallback(() => {
    trackEvent('conversion', 'upgrade_banner_clicked', 'upgrade_container');
    if (plusFeature) {
      openModal('PlusFeatureModal', { feature: plusFeature });
      return;
    }
    openModal('PricingModal', {
      title: plusTitle,
      subtitle: plusSubtitle,
    });
  }, [plusFeature, plusTitle, plusSubtitle, openModal]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Icon 
          name={icon}
          size={200} 
          color={colors.text_color_primary} 
          style={styles.backgroundIcon}
        />
        
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Icon name={icon} size={isXS ? 24 : 28} color={colors.text_color_primary} />
          </View>
          
          <Text weight="bold" style={styles.title}>
            {displayTitle}
          </Text>
          
          <Text size="m" color="secondary" style={styles.subtitle}>
            {displaySubtitle}
          </Text>
        </View>

        {Array.isArray(features) && features.length > 0 && (
          <View style={styles.features}>
            {features.map((feature, index) => (
              <Pressable 
                key={index} 
                style={styles.feature}
                onPress={onAction}
              >
                <View style={styles.featureIcon}>
                  <Icon 
                    name={feature.icon} 
                    size={20} 
                    color={colors.text_color_primary} 
                  />
                </View>
                <View style={styles.featureContent}>
                  <Text weight="medium" style={styles.featureTitle}>
                    {feature.title || feature.text}
                  </Text>
                  {feature.description && (
                    <Text size="s" color="secondary" style={styles.featureDescription}>
                      {feature.description}
                    </Text>
                  )}
                </View>
              </Pressable>
            ))}
          </View>
        )}

        <View style={styles.buttonContainer}>
          <Button
            isFullWidth
            text={displayActionText}
            onPress={handleOpenModal}
            variant="plus"
            size="large"
            style={styles.actionButton}
            textStyle={styles.actionButtonText}
            appendIcon="arrow-right"
          />
        </View>
      </View>
    </View>
  );
};

UpgradeContainer.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.string,
    description: PropTypes.string,
  })),
  icon: PropTypes.string,
  actionText: PropTypes.string,
  plusTitle: PropTypes.string,
  plusSubtitle: PropTypes.string,
  plusFeature: PropTypes.string,
};

export default React.memo(UpgradeContainer);
